<!-- Previous template section remains unchanged -->
<template>
    <div
        class="surface-ground flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
        <div class="fullwcontent flex flex-column align-items-center justify-content-center w-full">
            <div v-if="!loading" class="fullwcontent flex flex-row bg-white border-round shadow-2 w-11 lg:w-8 mx-3">
                <div class="flex-1 py-6 px-6">
                    <div class="text-center">
                        <div class="logo-container mb-5">
                            <div class="logo">
                                <img src="../../assets/Img/Logo/MoceroLogo.png" alt="Logo" />
                            </div>
                            <div class="text-900 text-3xl font-medium mb-2">Mocero One Platform</div>
                            <span class="text-600 font-medium">A product of Mocero Health</span>
                        </div>
                    </div>
                    <div class="form-container">
                        <form @submit.prevent="login" class="login-form">
                            <div class="field">
                                <MOPInputText v-model="loginData.username" label="Email Address" name="username"
                                    placeholder="Enter your email" :required="true" autocomplete="username"
                                    class="w-full" />
                            </div>
                            <div class="field">
                                <MOPPassword v-model="loginData.password" name="password" label="Password"
                                    placeholder="Enter your password" :required="true" :feedback="false"
                                    :toggleMask="true" autocomplete="current-password" class="w-full"
                                    inputClass="w-full" />
                            </div>
                            <div class="flex align-items-center justify-content-end mb-4">
                                <router-link to="/auth/forgot-password" class="forgot-link">
                                    Forgot password?
                                </router-link>
                            </div>
                            <MOPButton type="submit" label="Sign In" class="w-full p-3 signin-button"
                                :loading="loading" />
                            <div v-if="errorMessage" class="mt-4 text-center">
                                <small :class="['message', loginmsgdata.class]">{{ errorMessage }}</small>
                            </div>
                            <div class="mt-4 text-center">
                                <p class="help-text">Need any help? <span class="help-link">Click Here</span></p>
                            </div>
                        </form>
                    </div>
                </div>
                <div class="hidden lg:flex flex-1 p-4">
                    <div class="hidden lg:flex flex-1 slider-section">
                        <div class="relative w-full max-w-md mx-auto">
                            <!-- Carousel wrapper -->
                            <div class="relative overflow-hidden rounded-lg h-full">
                                <!-- Carousel items -->
                                <div v-for="(slide, index) in carouselSlides" :key="index" :class="[
                                    'absolute inset-0 transition-transform duration-500 ease-in-out',
                                    currentSlide === index ? 'translate-x-0' :
                                        currentSlide > index ? '-translate-x-full' : 'translate-x-full'
                                ]">
                                    <div class="flex flex-col items-center justify-center text-center text-white">
                                        <div class="mt-8">
                                            <h3 class="text-lg font-semibold text-white">{{ slide.title }}</h3>
                                            <p class="text-sm leading-relaxed mb-[30px] opacity-90">{{ slide.description
                                            }}
                                            </p>
                                        </div>
                                        <img :src="slide.image" :alt="slide.title" class="w-80 h-80 object-contain" />
                                    </div>
                                </div>
                            </div>

                            <!-- Slider indicators -->
                            <div class="absolute z-30 flex space-x-2 -translate-x-1/2 bottom-5 left-1/2">
                                <button v-for="(slide, index) in carouselSlides" :key="index" type="button" :class="[
                                    'h-2 rounded-full transition-colors duration-300',
                                    currentSlide === index ? 'bg-white w-7' : 'bg-white/50 w-2'
                                ]" @click="currentSlide = index"></button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <div v-if="loading" class="flex align-items-center justify-content-center">
                <MOPProgressSpinner />
            </div>
        </div>
        <MOPDialog v-model="showRoleDialog" modal header="Choose Role" class="role-dialog">
            <div class="roles-container">
                <div v-for="role in roles" :key="role.RoleId" class="role-card"
                    @click="selectRole(role.UserId, role.RoleId, role.RoleName, role.DeptRoleMapId)">
                    <span class="role-name">{{ role.RoleName }}</span>
                </div>
            </div>
        </MOPDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import type { LoginResult, UserData, UserDataToStore, RolesData } from '../../types/auth'
import LoginService from '../../services/auth/LoginService'

defineOptions({
    name: 'LoginView'
})

const router = useRouter()
const authStore = useAuthStore()
const loading = ref(false)
const errorMessage = ref('')
const showRoleDialog = ref(false)
const roles = ref<LoginResult[]>([])
const loginmsgdata = ref({
    loginmsg: '',
    class: ''
})
const loginData = ref({
    username: '',
    password: ''
})

// Carousel data and state
const currentSlide = ref(0)
const carouselSlides = ref([
    {
        title: 'Modern Healthcare Platform',
        description: 'A comprehensive solution for managing healthcare operations efficiently.',
        image: '/src/assets/Icons/CommonIcons/slider-1.png'
    },
    {
        title: 'Advanced Patient Care',
        description: 'Streamline patient management with our intuitive healthcare tools.',
        image: '/src/assets/Icons/CommonIcons/slider-2.png'
    },
    {
        title: 'Secure & Reliable',
        description: 'Built with enterprise-grade security and reliability in mind.',
        image: '/src/assets/Icons/CommonIcons/slider-3.png'
    }
])

let carouselInterval: number | null = null

// Carousel functions
const nextSlide = () => {
    currentSlide.value = (currentSlide.value + 1) % carouselSlides.value.length
}

const previousSlide = () => {
    currentSlide.value = currentSlide.value === 0
        ? carouselSlides.value.length - 1
        : currentSlide.value - 1
}

const startCarouselAutoplay = () => {
    carouselInterval = window.setInterval(() => {
        nextSlide()
    }, 5000) // Change slide every 5 seconds
}

const stopCarouselAutoplay = () => {
    if (carouselInterval) {
        clearInterval(carouselInterval)
        carouselInterval = null
    }
}

// Clear error message when component mounts
onMounted(() => {
    errorMessage.value = ''
    loginmsgdata.value.class = ''
    startCarouselAutoplay();

})



// Clean up carousel interval when component unmounts
onUnmounted(() => {
    stopCarouselAutoplay()
})

const isLoginResult = (item: unknown): item is LoginResult => {
    return typeof item === 'object' && item !== null && 'RoleId' in item
}

const login = async () => {
    try {
        // router.push('/accounts/companies')
        loading.value = true
        errorMessage.value = ''

        // Clear any existing auth data
        localStorage.removeItem('token')
        localStorage.removeItem('UsersData')
        localStorage.removeItem('RolesData')
        authStore.clearAuth()

        const response = await LoginService.GetRoles(
            loginData.value.username,
            loginData.value.password
        )

        console.log('Login response:', response)

        if (Array.isArray(response.result)) {
            if (response.result.length === 0) {
                errorMessage.value = 'No roles found'
                loginmsgdata.value.class = 'error'
                return
            }

            const firstResult = response.result[0]
            if (typeof firstResult === 'string') {
                if (firstResult === 'Invalid') {
                    errorMessage.value = 'Invalid username or password'
                    loginmsgdata.value.class = 'error'
                }
                return
            }

            if (isLoginResult(firstResult)) {
                if (response.result.length > 1) {
                    roles.value = response.result.filter(isLoginResult)
                    showRoleDialog.value = true
                } else {
                    await selectRole(
                        firstResult.UserId,
                        firstResult.RoleId,
                        firstResult.RoleName,
                        firstResult.DeptRoleMapId
                    )
                }
            }
        }
    } catch (error) {
        console.error('Login error:', error)
        errorMessage.value = 'An error occurred during login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}

const selectRole = async (userId: number, roleId: number, roleName: string, deptRoleMapId: number) => {
    try {
        loading.value = true
        showRoleDialog.value = false
        const userResponse = await LoginService.Login(userId, 1)

        console.log('User response:', userResponse)

        if (!userResponse.result || !Array.isArray(userResponse.result) || userResponse.result.length === 0) {
            throw new Error('Invalid user data received')
        }

        const userData: UserData = userResponse.result[0]
        console.log('User data:', userData)

        const userDataToStore: UserDataToStore = {
            OrganizationId: userData.OrganizationId,
            OrganizationCode: userData.OrganizationCode,
            BranchId: userData.BranchId,
            BranchCode: userData.BranchCode,
            UserId: userId,
            UserName: userData.UserName,
            UserStatus: userData.IsActive,
            phoneNumber: userData.MobileNumber,
            HospitalId: userData.OrganizationId,
            HospitalCode: userData.HospitalCode,
            HospitalBranchId: userData.HospitalBranchId,
            HospitalBranchCode: userData.HospitalBranchCode,
            ApplicationType: userData.ApplicationType,
            CountryId: userData.CountryId,
            AgentNumber: userData.AgentNumber,
            ProductId: userData.ProductId,
            AddressVar: userData.AddressVar,
            BranchStateId: userData.BranchStateId,
            Admin: userData.IsAdmin,
            AgentId: userData.AgentId == null ? 0 : userData.AgentId,
            AgentStatus: userData.AgentStatus,
            AgentDid: userData.AllowedDidNumbers == null ? 0 : userData.AllowedDidNumbers,
            TaxType: userData.TaxType == null ? 2 : 1,
            IsRequestExpired: userData.IsRequestExpired,
            ExpiredDuration: userData.ExpiredDuration
        }

        // Generate a token using user data
        const token = btoa(JSON.stringify({
            userId,
            roleId,
            organizationId: userData.OrganizationId,
            timestamp: new Date().getTime()
        }))

        // Update auth store
        authStore.setToken(token)
        authStore.setUser({
            id: userId,
            organizationId: userData.OrganizationId,
            roleId: roleId,
            deptRoleMapId: deptRoleMapId,
            email: loginData.value.username,
            name: userData.UserName,
            branchId: userData.BranchId,
            isAdmin: userData.IsAdmin == 1 ? true : false
        })
        authStore.setEntityId(0) // Set appropriate value if available
        authStore.setOrganizationId(userData.OrganizationId)
        authStore.setBranchId(userData.BranchId)

        console.log('Auth store updated:', {
            user: authStore.user,
            organizationId: authStore.user?.organizationId,
            token,
            sessionId: localStorage.getItem('sessionId')
        })

        // Store in localStorage
        localStorage.setItem('UsersData', JSON.stringify(userDataToStore))
        localStorage.setItem('token', token)

        const rolesData: RolesData = {
            RoleId: roleId,
            RoleName: roleName,
            DeptRoleMapId: deptRoleMapId
        }

        localStorage.setItem('RolesData', JSON.stringify(rolesData))

        loginmsgdata.value.loginmsg = 'Login Successfully'
        loginmsgdata.value.class = 'success'

        // Navigate to dashboard
        // router.push('/accounts/dashboard')
        router.push('/dashboard')
    } catch (error) {
        console.error('Role selection error:', error)
        errorMessage.value = 'An error occurred while processing your login'
        loginmsgdata.value.class = 'error'
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.surface-ground {
    background-color: var(--surface-ground);
    min-height: 100vh;
}

.logo-container {
    text-align: center;
}

.logo {
    background-color: #f7f7f8;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
}

.logo img {
    width: 40px;
    height: 40px;
}

.form-container {
    max-width: 400px;
    margin: 0 auto;
}

.field {
    margin-bottom: 1.5rem;
}

.field label {
    color: var(--text-color);
    font-weight: 500;
}

.field :deep(.p-password),
.field :deep(.p-inputtext) {
    width: 100%;
}

.field :deep(.p-password-input) {
    width: 100%;
    padding: 0.75rem;
}

.forgot-link {
    color: var(--primary-color);
    text-decoration: none;
    font-size: 0.875rem;
}

.signin-button {
    background-color: var(--primary-color);
    border: none;
    margin-top: 0.5rem;
}

.signin-button:hover {
    background-color: var(--primary-600);
}

.message {
    display: block;
    padding: 0.5rem;
    border-radius: 4px;
}

.message.error {
    color: var(--red-500);
    background-color: var(--red-50);
}

.message.success {
    color: var(--green-500);
    background-color: var(--green-50);
}

.help-text {
    color: var(--text-color-secondary);
    font-size: 0.875rem;
    margin: 0;
}

.help-link {
    color: var(--primary-color);
    cursor: pointer;
}

.slider-section {
    background: linear-gradient(135deg, var(--primary-color), var(--primary-800));
    border-radius: 15px;
    /* border-top-right-radius: 6px;
    border-bottom-right-radius: 6px; */
    /* padding: 2rem; */
}

.role-dialog {
    max-width: 600px;
}

.roles-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1rem;
}

.role-card {
    background-color: var(--surface-100);
    padding: 1rem;
    border-radius: 6px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
}

.role-card:hover {
    background-color: var(--surface-200);
    transform: translateY(-2px);
}

.fullwcontent {
    width: 100%;
}

.role-name {
    color: var(--text-color);
    font-weight: 500;
}

@media screen and (max-width: 991px) {
    .slider-section {
        display: none;
    }
}

@media screen and (max-width: 576px) {
    .form-container {
        padding: 0 1rem;
    }
}
</style>
